import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { Button } from '../../components/ui/button';
import { Shield, Building2, Tag, LogOut, BarChart3, ToggleLeft, LifeBuoy, Megaphone } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/superadmin/dashboard', label: 'Dashboard', icon: BarChart3 },
  { path: '/superadmin/organizations', label: 'Organizations', icon: Building2 },
  { path: '/superadmin/support', label: 'Support', icon: LifeBuoy },
  { path: '/superadmin/announcements', label: 'Announcements', icon: Megaphone },
  { path: '/superadmin/feature-flags', label: 'Features', icon: ToggleLeft },
  { path: '/superadmin/promo-codes', label: 'Promo Codes', icon: Tag },
];

export default function SALayout({ children }) {
  const { email, logout, isAuthenticated } = useSuperAdmin();
  const location = useLocation();
  const navigate = useNavigate();

  if (!isAuthenticated) {
    navigate('/superadmin/login', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sa-layout">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-bold text-slate-900">Inspiro Super Admin</span>
          </div>
          <nav className="hidden sm:flex items-center gap-1">
            {NAV_ITEMS.map(item => {
              const active = location.pathname === item.path || (item.path !== '/superadmin/dashboard' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`sa-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-3.5 h-3.5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 hidden sm:block">{email}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/superadmin/login'); }} className="h-8 text-xs text-slate-500 hover:text-red-600" data-testid="sa-logout-btn">
              <LogOut className="w-3.5 h-3.5 mr-1" /> Logout
            </Button>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="sm:hidden flex border-t border-slate-100 px-2">
          {NAV_ITEMS.map(item => {
            const active = location.pathname === item.path || (item.path !== '/superadmin/dashboard' && location.pathname.startsWith(item.path));
            return (
              <Link key={item.path} to={item.path} className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium ${active ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-500'}`}>
                <item.icon className="w-3.5 h-3.5" />
                {item.label}
              </Link>
            );
          })}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {children}
      </main>
    </div>
  );
}
